import { backend_api_v2 } from '/src/utils/apiv2'
import base from "./base"
import bus from '/src/utils/event_bus'
import { defineStore } from 'pinia'
import { sleep } from "@/utils/time";
import { uploadPicture } from "@/utils/checklist_pictures";
import { useCloudstorageStore } from "@/stores/cloudstorage";
import { useSelectedStore } from "@/stores/selected";
import { useTranslationsStore } from "@/stores/translations";
import { refusalText } from "@/utils/refusals";


export const useRecordsStore = defineStore('records', {
    state: () => ({
        rcp: null,
        rcp_loading: false,
        issues: [],


        // Pictures being uploaded right now, keyed by package + task.
        //
        // In the store and not in the row because the upload is no longer
        // small: a full-resolution photo is megabytes, and the operator closes
        // the checklist, switches panel or lets the right panel re-render long
        // before it lands. An unmounted row must not be able to orphan an
        // upload or lose the tick that follows it, and reopening the card has
        // to show the same pending tile.
        picture_uploads: {},

        // queue state
        chi_queue: [],
        // The in-flight drain, or null. Not a boolean: every caller awaits this
        // same promise, which is what makes claiming the drain atomic.
        chi_flush: null,
    }),
    getters: {
        rcpIsRunning: state => {
            // If not start date, it is not running.
            if (!state.rcp?.start_date) return false
            // If paused, it is not running.
            if (state.rcp?.paused) return false
            // Check that for all the records in state.rcp.checklist, if there is a record
            // with name "STATION_STARTED" there is a record with name "STATION_JOB_READY
            // for each production_station value. If missing, its running because it's not paused.
            for (let check of state.rcp.checklist) {
                if (check.text == "STATION_STARTED") {
                    if (!state.rcp.checklist
                        .find(c => c.text == "STATION_JOB_READY" &&
                            c.production_station == check.production_station)
                    ) {
                        return true
                    }
                }
            }
            // If has start date, it's not paused, and all started stations are ready, it can't be running.
            return false
        },
        rcpNotStarted: state => !state.rcp?.start_date,
        rcpIsReady: state => state.rcp?.ready_date,
    },
    actions: {
        setRCP(rcp) { this.rcp = rcp },
        loadingRCP(b) { this.rcp_loading = b },

        // Issues
        setIssues: base.actions.setter("issues"),
        replaceIssue: base.actions.updater("issues"),
        createIssue: base.actions.creator("issues"),
        // Merged, not replaced: a saved issue comes back without the fields
        // the company-wide list adds (project, line, ...), and replacing the
        // row would blank its Project and Line cells.
        updateIssue(updated) {
            const i = this.issues.findIndex(obj => obj.uuid == updated?.uuid)
            if (i == -1) return
            this.issues[i] = { ...this.issues[i], ...updated }
            this.issues = [...this.issues]
        },
        removeIssue: base.actions.remover("issues"),

        loadRCP(payload) {
            this.loadingRCP(true)
            let url = `records/rcps/?${payload.level}=${payload.uuid}`
            return backend_api_v2.get(url)
                .then(({ data }) => {
                    this.loadingRCP(false)
                    // If no proof
                    if (data.results.length == 0) {
                        // If no results, set null cause we may be changing actual.
                        this.setRCP(null)
                    } else {
                        // If results, set first.
                        this.setRCP(data.results[0])
                    }
                })
                .catch(e => {
                    console.log(`Cant load ready constructible proof: ${e}`)
                })
        },
        createRCP(payload) {
            let epk = payload.level == "assembly" ? "assemblies" : "modules"
            let url = `constructibles/${epk}/${payload.uuid}/create_proof/`
            return backend_api_v2.get(url)
                .then((response) => {
                    // And finally if really created, reload recursivelly.
                    if ([200, 201].includes(response.status)) {
                        return this.loadRCP(payload)
                    }
                })
                .catch(e => {
                    // The backend puts the reason in `detail` — "This
                    // constructible has no production line set." is the common
                    // one, and it tells the user exactly what to fix. Swallowing
                    // it left the caller to carry on with no proof and report
                    // success for work that never happened.
                    const data = e.response?.data || {}
                    let text = data.detail
                    // A refused unit says why in the operator's language
                    // instead of DRF's "No Assembly matches the given query."
                    if (e.response?.status == 404) {
                        const selected_store = useSelectedStore()
                        selected_store.noteRefusal(payload.uuid, payload.level, e)
                        if (data.code) text = refusalText(useTranslationsStore().translate, data)
                    }
                    console.log(`Cant create proof: ${text || e}`)
                    this.setRCP(null)
                    bus.emit("notification", {
                        timeout: 6000,
                        text: text || "Can't create the proof.",
                        color: "error",
                    })
                    throw e
                })
        },
        putRCP(proof) {
            return backend_api_v2.put(`records/rcps/${proof.uuid}/`, proof)
                .then(({ data }) => {
                    this.setRCP(data)
                })
        },
        deleteRCP(proof_uuid) {
            return backend_api_v2.delete(`records/rcps/${proof_uuid}/`)
                .then(() => {
                    this.setRCP(null)
                })
        },
        multiRCPFlushByActualUuids(actuals_uuids) {
            return backend_api_v2.post('constructibles/actuals/bulk_proof_flush/', actuals_uuids)
                .catch(e => { console.log(`Cant batch flush proofs actuals: ${e}`) })
        },
        multiRCPSetReadyByActualUuids(actuals_uuids, set_all_stations_ready = false) {
            return backend_api_v2.post('constructibles/actuals/bulk_proof_ready/', {
                uuids: actuals_uuids,
                set_all_stations_ready,
            })
                .catch(e => { console.log(`Cant set ready for proofs actuals: ${e}`) })
        },
        mergeRCPsByActualUuids(actuals_uuids) {
            return backend_api_v2.post('constructibles/actuals/bulk_proof_merge/', actuals_uuids)
                .catch(e => { console.log(`Cant merge proofs actuals: ${e}`) })
        },

        async testFunction() {
            console.log("TEST FUNCTION IN RECORDS STORE...")
            await sleep(1000)
        },

        /** The pictures still in flight for one task. */
        pendingPictures(key) {
            return this.picture_uploads[key] || []
        },

        /** Upload one picture and, if asked, tick the task it belongs to.
         *
         * `tick` is the checklist item to post on success, or null when the
         * task is waiting on something else too (an unfilled form, an empty QR
         * field). `post` is how to post it -- the multipanel dialog passes its
         * own, so the item lands on the right member's proof.
         *
         * The tick fires from the upload result and never from a file count:
         * that count only moves after the package refetch, which is a debounce
         * and a round trip behind the photo.
         */
        async uploadTaskPicture({ key, pck_uuid, file, meta, tick, post }) {
            const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
            const url = URL.createObjectURL(file)
            if (!this.picture_uploads[key]) this.picture_uploads[key] = []
            this.picture_uploads[key].push({ uid, url, cf_uuid: null })

            const cloudstorage = useCloudstorageStore()
            let cloud_file = null
            try {
                cloud_file = await uploadPicture({
                    pck_uuid,
                    file,
                    meta,
                    deleteFile: (uuid) => cloudstorage.deleteCloudFile(uuid),
                })
            } catch (e) {
                this.dropPendingPicture(key, uid)
                throw e
            }

            // The tile stays until the caller has the real file on screen, so
            // there is no frame with neither. `settlePendingPictures` drops it.
            const entry = (this.picture_uploads[key] || []).find(p => p.uid === uid)
            if (entry) entry.cf_uuid = cloud_file?.uuid || null
            if (!entry || !entry.cf_uuid) this.dropPendingPicture(key, uid)

            if (tick) {
                const send = post || ((chi) => this.postChecklistItem(chi))
                // Stamped here and not where `tick` was built: that is a Vue
                // computed, so its clock would read whenever the row last
                // rendered rather than when the photo actually landed.
                await send({ ...tick, timestamp: new Date() })
            }
            return cloud_file
        },

        /** Forget a pending tile and release its object URL. */
        dropPendingPicture(key, uid) {
            const list = this.picture_uploads[key]
            if (!list) return
            const i = list.findIndex(p => p.uid === uid)
            if (i === -1) return
            URL.revokeObjectURL(list[i].url)
            list.splice(i, 1)
            if (!list.length) delete this.picture_uploads[key]
        },

        /** Drop every pending tile whose file is now in `files`. */
        settlePendingPictures(key, files) {
            const list = this.picture_uploads[key]
            if (!list || !list.length) return
            const uuids = new Set((files || []).map(f => f.uuid))
            for (const p of [...list]) {
                if (p.cf_uuid && uuids.has(p.cf_uuid)) this.dropPendingPicture(key, p.uid)
            }
        },

        async postChecklistItem(item) {
            // Without a proof there is nothing to post to. This used to reach
            // `this.rcp.uuid` and throw a TypeError inside the loop below,
            // where the catch swallowed it — so the caller awaited, saw no
            // error, and announced the panel as started.
            this.enqueueChecklistItem(item)
            return this.flushChecklistItems()
        },

        /** Queues one item without sending anything.
         *
         * For a caller that has several items to write and wants them in one
         * request: queue them all, then await `flushChecklistItems` once.
         * `postChecklistItem` is this plus an immediate flush, which is the
         * right shape for a single checkbox but the wrong one for a burst —
         * awaiting each push empties the queue between items and there is
         * nothing left to batch.
         *
         * Synchronous on purpose, so a loop of these leaves no floating
         * promises behind.
         */
        enqueueChecklistItem(item) {
            if (!this.rcp) throw new Error("No ready constructible proof to post to.")
            this.chi_queue.push(item);
        },

        /** Drains `chi_queue` in as few requests as it can.
         *
         * The queue was always here and always drained everything in it — but
         * every caller awaited its own push, so the next item was only queued
         * once the previous one had come back and the queue was empty again.
         * Starting a panel at station N+1 therefore sent 2N+1 separate
         * `post_chi` calls, each re-deriving every station status and rewriting
         * the whole assembly row. Batching happens here rather than at the call
         * site so that every caller gets it: the per-task checkbox, the station
         * form and the force-start flow alike.
         *
         * One shared promise instead of the polled `chi_is_processing` flag.
         * Two callers could clear `while (chi_is_processing)` in the same tick,
         * both claim the drain and post concurrently to the same proof — and
         * since every response carries the whole re-serialized proof,
         * whichever landed last overwrote the other's items in the store, so a
         * ticked checkbox could un-tick itself on screen. Handing every caller
         * the same promise makes the claim atomic, and it also means a caller
         * that joins an in-flight drain resolves when its item has actually
         * been written rather than up to 200 ms afterwards.
         */
        async flushChecklistItems() {
            if (this.chi_flush) return this.chi_flush
            this.chi_flush = (async () => {
                try {
                    // Re-checked after every request, so items queued while one
                    // is in flight leave in the next batch instead of waiting
                    // for the operator's next tap to drain them.
                    while (this.chi_queue.length > 0) {
                        const items = this.chi_queue.splice(0)
                        const r = await backend_api_v2.post(
                            `records/rcps/${this.rcp.uuid}/post_chis/`, items)
                        this.setRCP(r.data)
                    }
                } catch (error) {
                    // Still swallowed: surfacing it is F9, and no caller clears
                    // its spinner in a `finally` yet, so throwing here would
                    // leave rows spinning forever. What is fixed is the leak —
                    // anything queued during the failed request is dropped
                    // instead of riding along with the next tap.
                    this.chi_queue.splice(0)
                    console.error('Error processing checklist items!:', error);
                } finally {
                    this.chi_flush = null
                }
            })()
            return this.chi_flush
        },


        // Issues
        loadIssues: base.actions.loader_pr("records/issues/", "Issues"),
        // Company-wide: `{created__date__gte, created__date__lte}` plus the
        // extended fields, since no project's designs are loaded there.
        loadCompanyIssues: base.actions.loader_query("records/issues/", "Issues"),
        postIssue: base.actions.poster("records/issues/", "Issue"),
        putIssue: base.actions.putter("records/issues/", "Issue"),
        deleteIssue: base.actions.deleter("records/issues/", "Issue"),
        refreshIssue: base.actions.refresher("records/issues/", "Issue"),
    },
})
